import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import useFAQSchema from '../hooks/useFAQSchema'
import useHowToSchema from '../hooks/useHowToSchema'
import BulkThumbnailExtractor from '../organisms/BulkThumbnailExtractor'
import ThumbnailInsights from '../organisms/ThumbnailInsights'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import Spinner from '../atoms/Spinner'
import { setUrl, extractVideoId, clearThumbnailState, fetchVideoMeta } from '../store/slices/ytThumbnailSlice'
import './YtThumbnailPage.css'

const YtThumbnailPage = ({ tools = [] }) => {
  useCanonicalUrl();
  useFAQSchema();
  useHowToSchema('YouTube Thumbnail Downloader');
  const dispatch = useDispatch()
  const [copiedId, setCopiedId] = useState(null)
  const [showAllHistory, setShowAllHistory] = useState(false)
  const [fileSizes, setFileSizes] = useState({})
  const [unavailable, setUnavailable] = useState({})
  const [unavailableScenes, setUnavailableScenes] = useState({})
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('yt_thumb_history') || '[]')
    } catch {
      return []
    }
  })

  const saveToHistory = (id) => {
    setHistory(prev => {
      const updated = [id, ...prev.filter(h => h !== id)].slice(0, 5)
      localStorage.setItem('yt_thumb_history', JSON.stringify(updated))
      return updated
    })
  }
  const { url, videoId, thumbnails, sceneThumbnails, status, error, videoMeta, metaStatus } = useSelector(state => state.ytThumbnail)

  const handleUrlChange = (e) => {
    dispatch(setUrl(e.target.value))
  }
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText()
      dispatch(setUrl(text))
    } catch (err) {
      console.error('Clipboard read failed:', err)
    }
  }

  const handleExtract = () => {
    dispatch(extractVideoId())
  }

  const handleClear = () => {
    dispatch(setUrl(''))
    dispatch(clearThumbnailState())
  }

  const handleDownload = async (thumbnailUrl, quality) => {
    try {
      const response = await fetch(thumbnailUrl)
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `thumbnail_${videoId}_${quality}.jpg`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      console.error('Download failed:', err)
    }
  }
  const handleCopyUrl = async (thumbnailUrl, id) => {
    try {
      await navigator.clipboard.writeText(thumbnailUrl)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  }

  const handleDownloadAllQualities = async () => {
    const JSZip = (await import('jszip')).default
    const zip = new JSZip()

    for (const thumb of thumbnails) {
      if (unavailable[thumb.id]) continue
      try {
        const res = await fetch(thumb.url)
        const blob = await res.blob()
        zip.file(`${videoId}_${thumb.id}.jpg`, blob)
      } catch (err) {
        console.error(`Failed for ${thumb.id}:`, err)
      }
    }

    const content = await zip.generateAsync({ type: 'blob' })
    const downloadUrl = URL.createObjectURL(content)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = `${videoId}_all_thumbnails.zip`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(downloadUrl)
  }

  useEffect(() => {
    if (url) {
      const timer = setTimeout(() => {
        dispatch(extractVideoId())
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [url, dispatch])
  useEffect(() => {
    if (videoId) {
      dispatch(fetchVideoMeta(videoId))
    }
  }, [videoId, dispatch])
  useEffect(() => {
    if (thumbnails.length > 0) {
      thumbnails.forEach(async (thumb) => {
        try {
          const res = await fetch(thumb.url, { method: 'HEAD' })
          const size = res.headers.get('content-length')
          if (size) {
            setFileSizes(prev => ({ ...prev, [thumb.id]: (size / 1024).toFixed(0) }))
          }
        } catch (err) {
          // silent fail, size simply won't show
        }
      })
    }
  }, [thumbnails])
  useEffect(() => {
    if (videoId && status === 'success') {
      saveToHistory(videoId)
    }
  }, [videoId, status])

  useEffect(() => {
    // Update page title and meta
    document.title = 'YouTube Thumbnail Downloader - Bulk, Shorts, HD & Scene Thumbnails | EveryKits';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Free YouTube thumbnail downloader with bulk ZIP export, Shorts support, all auto-generated scene thumbnails, and a built-in brightness/contrast checklist. No signup, no watermark.');
    }

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "YouTube Thumbnail Downloader",
      "applicationCategory": "WebApplication",
      "operatingSystem": "All",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "Free online YouTube thumbnail downloader with bulk ZIP export, Shorts support, auto-generated scene thumbnails, and a thumbnail quality checklist.",
      "url": "https://everykits.com/yt-thumbnail",
      "featureList": [
        "Download HD, SD, HQ, and MQ thumbnails",
        "YouTube Shorts thumbnail support",
        "Bulk extraction from multiple URLs",
        "Download all thumbnails as ZIP",
        "Auto-generated scene thumbnail frames",
        "Thumbnail brightness and contrast checklist",
        "SERP and suggested-video preview",
        "One-click copy image URL"
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'software-application');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <ToolPageTemplate
      title="YouTube Thumbnail Downloader"
      description="Download HD YouTube thumbnails in bulk, grab Shorts thumbnails, and check quality before you upload — all free, no signup"
      tools={tools}
    >
      <div>
        <section className="yt-thumbnail">
          <div className="thumbnail-input-section">
            <Input
              label="YouTube Video URL"
              value={url}
              onChange={handleUrlChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  dispatch(extractVideoId())
                }
              }}
              placeholder="https://www.youtube.com/watch?v=..."
            />

            <button className="paste-btn" onClick={handlePasteFromClipboard} type="button">
              Paste from Clipboard
            </button>

            <div className="thumbnail-buttons">
              <Button onClick={handleExtract} disabled={!url || status === 'loading'}>
                {status === 'loading' ? <Spinner size="small" /> : null}
                Extract Thumbnails
              </Button>
              <Button variant="secondary" onClick={handleClear}>
                Clear
              </Button>
            </div>

            <div className="example-chips">
              <span className="example-label">Try an example:</span>
              <button
                className="example-chip"
                onClick={() => dispatch(setUrl('https://www.youtube.com/watch?v=dQw4w9WgXcQ'))}
              >
                Music Video
              </button>
              <button
                className="example-chip"
                onClick={() => dispatch(setUrl('https://www.youtube.com/watch?v=jNQXAC9IVRw'))}
              >
                First YouTube Video
              </button>
            </div>

            {history.length > 0 && (
              <div className="history-chips">
                <span className="history-label">Recent:</span>
                {(showAllHistory ? history : history.slice(0, 3)).map((id) => (
                  <button
                    key={id}
                    className="history-chip"
                    onClick={() => dispatch(setUrl(`https://youtube.com/watch?v=${id}`))}
                  >
                    {id}
                  </button>
                ))}
                {history.length > 3 && (
                  <button
                    className="history-toggle"
                    onClick={() => setShowAllHistory(!showAllHistory)}
                  >
                    {showAllHistory ? 'Show less' : `+${history.length - 3} more`}
                  </button>
                )}
              </div>
            )}
          </div>

          {error && (
            <ResultBox title="Error">
              <div className="error-message">{error}</div>
            </ResultBox>
          )}

          {videoId && thumbnails.length > 0 && (
            <ResultBox title={metaStatus === 'succeeded' && videoMeta ? videoMeta.title : `Thumbnails for Video ID: ${videoId}`}>
              {videoMeta?.author_name && (
                <p className="video-channel-name">
                  Channel: <a href={videoMeta.author_url} target="_blank" rel="noopener noreferrer">{videoMeta.author_name}</a>
                </p>
              )}
              <Button size="small" variant="secondary" onClick={handleDownloadAllQualities}>
                Download All Qualities (ZIP)
              </Button>
              <div className="thumbnail-grid">
                {thumbnails.map((thumbnail) => (
                  <div key={thumbnail.id} className="thumbnail-item">
                    <div className="thumbnail-preview">
                      <img
                        src={thumbnail.url}
                        alt={thumbnail.quality}
                        loading="lazy"
                        onLoad={(e) => {
                          if (e.target.naturalWidth === 120) {
                            setUnavailable(prev => ({ ...prev, [thumbnail.id]: true }))
                          }
                        }}
                      />
                      {unavailable[thumbnail.id] && (
                        <span className="unavailable-badge">Not available for this video</span>
                      )}
                    </div>
                    <div className="thumbnail-info">
                      <h4>{thumbnail.quality}</h4>
                      <p className="thumbnail-dimensions">
                        {thumbnail.id === 'maxresdefault' && '1280×720'}
                        {thumbnail.id === 'hqdefault' && '480×360'}
                        {thumbnail.id === 'mqdefault' && '320×180'}
                        {thumbnail.id === 'sddefault' && '640×480'}
                        {fileSizes[thumbnail.id] && ` · ${fileSizes[thumbnail.id]} KB`}
                      </p>
                      <div className="thumbnail-actions">
                        <Button
                          size="small"
                          onClick={() => handleDownload(thumbnail.url, thumbnail.id)}
                        >
                          Download
                        </Button>
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => handleCopyUrl(thumbnail.url, thumbnail.id)}
                        >
                          {copiedId === thumbnail.id ? 'Copied!' : 'Copy URL'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {sceneThumbnails.length > 0 && (
                  <div className="scene-thumbnails-section">
                    <h4 className="scene-section-title">Alternate Scene Thumbnails (Auto-Generated by YouTube)</h4>
                    <p className="scene-section-desc">
                      These are frames YouTube auto-generates from the video. For videos with a custom uploaded thumbnail, these may look identical to the default — this works best on videos without a custom thumbnail.
                    </p>
                    <div className="scene-grid">
                      {sceneThumbnails.map((scene) => (
                        !unavailableScenes[scene.id] && (
                          <div key={scene.id} className="scene-item">
                            <img
                              src={scene.url}
                              alt={scene.label}
                              loading="lazy"
                              onLoad={(e) => {
                                if (e.target.naturalWidth === 120) {
                                  setUnavailableScenes(prev => ({ ...prev, [scene.id]: true }))
                                }
                              }}
                            />
                            <div className="scene-item-footer">
                              <span>{scene.label}</span>
                              <button
                                className="scene-download-btn"
                                onClick={() => handleDownload(scene.url, scene.id)}
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
                <ThumbnailInsights imageUrl={thumbnails.find(t => t.id === 'hqdefault')?.url} />
              </div>
              {videoId && thumbnails.length > 0 && (
                <div className="serp-preview">
                  <p className="serp-preview-label">Preview in YouTube suggestions:</p>
                  <div className="serp-card">
                    <img
                      src={thumbnails.find(t => t.id === 'hqdefault')?.url}
                      alt="preview"
                      className="serp-thumb"
                    />
                    <div className="serp-meta">
                      <div className="serp-title">{videoMeta?.title || 'Video Title'}</div>
                      <div className="serp-channel">{videoMeta?.author_name || 'Channel Name'}</div>
                    </div>
                  </div>

                  <div className="share-buttons">

                    <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Check this YouTube thumbnail:')}&url=${encodeURIComponent(thumbnails.find(t => t.id === 'hqdefault')?.url || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn share-twitter"
                    >
                      Share on X
                    </a>

                    <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent((thumbnails.find(t => t.id === 'hqdefault')?.url || '') + ' - via EveryKits')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn share-whatsapp"
                    >
                      Share on WhatsApp
                    </a>

                    <a href={`https://pinterest.com/pin/create/button/?media=${encodeURIComponent(thumbnails.find(t => t.id === 'hqdefault')?.url || '')}&description=${encodeURIComponent('YouTube Thumbnail')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn share-pinterest"
                    >
                      Pin It
                    </a>
                  </div>
                </div>
              )}
            </ResultBox>
          )}

          {status === 'loading' && (
            <div className="loading-section">
              <Spinner size="large" />
              <p>Extracting thumbnails...</p>
            </div>
          )}

          <div className="bulk-divider">
            <span>OR</span>
          </div>
          <BulkThumbnailExtractor />
        </section>

        <section className="tool-guide">
          <div className="guide-section">
            <h2>How to Use YouTube Thumbnail Downloader</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Paste the YouTube URL</h3>
                  <p>Copy the URL of any YouTube video and paste it into the input field. It works with any standard YouTube link format, including shortened youtu.be links.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Extract Thumbnails</h3>
                  <p>Click extract and the tool pulls all available thumbnail resolutions for that video directly from YouTube — no login or API key needed on your end.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Preview Each Quality</h3>
                  <p>Compare the available resolutions — HD (1280×720), SD (640×480), HQ (480×360), and MQ (320×180) — side by side before choosing one.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Download</h3>
                  <p>Click download on whichever resolution you need, and the thumbnail saves directly to your device.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">5</div>
                <div className="step-content">
                  <h3>Need thumbnails from several videos?</h3>
                  <p>Scroll down to the bulk section, paste multiple YouTube URLs (one per line), and download every thumbnail in one ZIP file instead of extracting them one at a time.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Why Use This YouTube Thumbnail Downloader</h2>
            <p>This tool extracts every thumbnail YouTube generates for a video — not just one image, but all standard resolutions, all auto-generated scene frames, and Shorts thumbnails — in a single paste. It also checks the thumbnail's brightness and contrast before you reuse it, something most downloaders skip entirely.</p>
            <p>YouTube generates several thumbnail sizes and multiple candidate frames for every video, but there's no built-in way to browse them directly on the site. Content creators use this to reference their own videos, marketers use it for promotional material, and researchers use the bulk mode to audit thumbnails across many videos at once.</p>
            <p>Extraction works entirely by reading the video ID out of the URL and requesting YouTube's publicly available thumbnail images — no video content is downloaded, and nothing you paste is stored on our servers.</p>
          </div>
          <div className="guide-section">
            <h2>What You Can Do With This Tool</h2>
            <div className="platform-guide-list">
              <div className="platform-guide-item">
                <h3>Download in bulk as ZIP</h3>
                <p>Paste multiple YouTube URLs at once and download every thumbnail as a single ZIP file — useful for channel audits or building a reference library.</p>
              </div>
              <div className="platform-guide-item">
                <h3>Grab Shorts thumbnails</h3>
                <p>Paste any youtube.com/shorts/ link and the tool extracts its thumbnail the same way it handles standard videos.</p>
              </div>
              <div className="platform-guide-item">
                <h3>See every auto-generated frame</h3>
                <p>Beyond the default thumbnail, YouTube also picks several candidate frames from the video itself. This tool surfaces all of them for videos that don't have a custom uploaded thumbnail.</p>
              </div>
              <div className="platform-guide-item">
                <h3>Check thumbnail quality before you reuse it</h3>
                <p>A built-in brightness and contrast checklist flags thumbnails that may look washed out or low-contrast in the YouTube feed, plus a safe-zone tip for where YouTube overlays its duration badge.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>Which YouTube URL formats are supported?</h3>
                <p>Standard youtube.com/watch?v= links, shortened youtu.be links, and YouTube Shorts URLs all work — the tool just needs the video ID, which it extracts automatically from any of these formats.</p>
              </div>
              <div className="faq-item">
                <h3>What thumbnail resolutions are available?</h3>
                <p>Up to four sizes depending on the video: HD (1280×720), SD (640×480), HQ (480×360), and MQ (320×180). Not every video has an HD thumbnail available, in which case the tool shows the highest resolution that does exist.</p>
              </div>
              <div className="faq-item">
                <h3>Does this work for private or unlisted videos?</h3>
                <p>No — thumbnails can only be extracted for public videos, since the tool relies on YouTube's publicly accessible thumbnail URLs.</p>
              </div>
              <div className="faq-item">
                <h3>Is downloading YouTube thumbnails legal?</h3>
                <p>Thumbnails are typically owned by the video's uploader or YouTube itself. Downloading one for reference, commentary, or personal use is generally fine, but check the applicable copyright terms before using someone else's thumbnail commercially.</p>
              </div>
              <div className="faq-item">
                <h3>Is any of my data saved when I use this tool?</h3>
                <p>No. The URL you paste is only used to extract the video ID and request the thumbnail — nothing is logged or stored.</p>
              </div>
              <div className="faq-item">
                <h3>Can I download thumbnails from YouTube Shorts?</h3>
                <p>Yes. Paste a youtube.com/shorts/ link the same way you would a regular video URL — the tool detects the format automatically and extracts the thumbnail.</p>
              </div>
              <div className="faq-item">
                <h3>Can I download thumbnails from multiple videos at once?</h3>
                <p>Yes. Use the bulk section below the main tool, paste one URL per line, and download every thumbnail in a single ZIP file instead of one at a time.</p>
              </div>
              <div className="faq-item">
                <h3>What are the "Alternate Scene Thumbnails"?</h3>
                <p>Beyond the default thumbnail, YouTube automatically picks up to four candidate frames from the video itself. This tool surfaces all four — though for videos with a custom uploaded thumbnail, they often look the same as the default image.</p>
              </div>
              <div className="faq-item">
                <h3>How do I know if my thumbnail is good before uploading?</h3>
                <p>The tool includes a brightness and contrast checklist that flags thumbnails that may look too dark, too bright, or low-contrast in the YouTube feed, along with a tip on which corner to keep clear of text.</p>
              </div>
            </div>
          </div>
          <div className="guide-section">
            <h2>Best Thumbnail Size for Each Platform</h2>
            <p>Different platforms crop or scale thumbnails differently, so choosing the right resolution matters for how sharp the image looks.</p>
            <div className="platform-guide-list">
              <div className="platform-guide-item">
                <h3>Blog posts & articles</h3>
                <p>Use HD (1280×720) — it stays sharp even when scaled down to fit article widths.</p>
              </div>
              <div className="platform-guide-item">
                <h3>Twitter/X Cards</h3>
                <p>SD (640×480) or HD works well — Twitter compresses images automatically on upload.</p>
              </div>
              <div className="platform-guide-item">
                <h3>Discord embeds</h3>
                <p>HQ (480×360) is enough since Discord displays embeds at a small fixed size.</p>
              </div>
              <div className="platform-guide-item">
                <h3>Presentations & documents</h3>
                <p>Always use HD (1280×720) for the clearest result when projected or zoomed in.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/image-compressor/" className="related-tool">
                <div className="tool-icon">Image Compressor</div>
                <div className="tool-description">Reduce image file size without losing visible quality</div>
              </Link>
              <Link to="/image-converter/" className="related-tool">
                <div className="tool-icon">Image Converter</div>
                <div className="tool-description">Convert images between JPG, PNG, and WebP</div>
              </Link>
              <Link to="/qr-generator/" className="related-tool">
                <div className="tool-icon">QR Code Generator</div>
                <div className="tool-description">Generate QR codes for URLs, text, and more</div>
              </Link>
            </div>
          </div>
        </section>


      </div>
    </ToolPageTemplate>
  )
}

export default YtThumbnailPage